// Earcons: short synthesised cues so the user can hear, without looking, when the mic is really
// listening, when it stopped, when it's their turn again and when it needs a tap. No audio
// files: oscillators with soft envelopes on the page's own (already unlocked) AudioContext.

export type CueKind = "listening" | "stopped" | "turn" | "tap";

export interface CueNote {
  /** Hz. */
  freq: number;
  /** Seconds from the cue's start. */
  at: number;
  /** Seconds. */
  dur: number;
  /** Peak gain, before the cue's volume. */
  gain: number;
}

/**
 * The cues. Sine tones, an octave-ish apart so they read as a chime rather than a beep:
 * - listening: a soft rising two-tone (about 220 ms);
 * - stopped: the same two tones falling (about 220 ms);
 * - turn: one quiet high tick (about 90 ms), when the reply ends and the mic is open again;
 * - tap: two low, even blips (about 260 ms), when the mic stopped and needs a tap.
 */
export const CUES: Record<CueKind, readonly CueNote[]> = {
  listening: [
    { freq: 660, at: 0, dur: 0.1, gain: 1 },
    { freq: 990, at: 0.09, dur: 0.13, gain: 1 },
  ],
  stopped: [
    { freq: 880, at: 0, dur: 0.1, gain: 0.9 },
    { freq: 587, at: 0.09, dur: 0.13, gain: 0.9 },
  ],
  turn: [{ freq: 1320, at: 0, dur: 0.09, gain: 0.4 }],
  tap: [
    { freq: 440, at: 0, dur: 0.1, gain: 0.9 },
    { freq: 440, at: 0.16, dur: 0.1, gain: 0.9 },
  ],
};

/** Overall cue volume: modest, well under a reply's speech. */
export const CUE_VOLUME = 0.12;

/** How long a cue lasts, in ms (its last note's end). */
export function cueMs(kind: CueKind): number {
  return Math.round(Math.max(...CUES[kind].map((n) => n.at + n.dur)) * 1000);
}

/** The bits of an AudioContext a cue needs (a fake in tests). */
export type CueContext = Pick<BaseAudioContext, "currentTime" | "createOscillator" | "createGain">;

/**
 * Schedules `kind` on `ctx` into `dest`, starting now. Each note is a sine with a quick (8 ms)
 * attack and an exponential decay, so nothing clicks. Returns the cue's length in ms.
 */
export function scheduleCue(ctx: CueContext, dest: AudioNode, kind: CueKind, volume = CUE_VOLUME): number {
  const t0 = ctx.currentTime + 0.01;
  for (const n of CUES[kind]) {
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    const start = t0 + n.at;
    const end = start + n.dur;
    osc.type = "sine";
    osc.frequency.setValueAtTime(n.freq, start);
    env.gain.setValueAtTime(0.0001, start);
    env.gain.exponentialRampToValueAtTime(Math.max(0.0002, n.gain * volume), start + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(env);
    env.connect(dest);
    osc.start(start);
    osc.stop(end + 0.02);
    osc.onended = () => env.disconnect();
  }
  return cueMs(kind);
}
